// Contesto allegato agli eventi: user, tag, extra, context, trace_id e cronologia
// breadcrumb. Specchio di `CloseYourIt::Scope` (gem) e `Scope` (dart). Nel browser
// c'è un holder globale singolo (single-thread); su Node il `Client` ne isola una
// copia per flusso asincrono via [fork] + AsyncLocalStorage (CYJS-7), così i dati
// di una richiesta non finiscono negli eventi di un'altra concorrente. La
// correlazione per-operazione si ottiene via [traceId].

import { Breadcrumb, BreadcrumbBuffer } from './breadcrumbs'
import type { BreadcrumbInput } from './breadcrumbs'
import { scrub } from './scrubber'
import type { EventPayload, UserContext } from './types'

export class Scope {
  readonly breadcrumbs: BreadcrumbBuffer

  private user: Record<string, unknown> | undefined
  private tags: Record<string, string> = {}
  private extra: Record<string, unknown> = {}
  private contexts: Record<string, Record<string, unknown>> = {}
  private request: Record<string, unknown> | undefined

  /// ID di correlazione condiviso da log/errori della stessa operazione/pagina.
  traceId: string | undefined

  /// ID della sessione di replay in corso (più longevo del `traceId`: copre
  /// l'intera sessione-browser). Allegato agli errori come `contexts.replay.replay_id`.
  replaySessionId: string | undefined

  /// `parent-id` (span-id del chiamante) del trace context W3C adottato con
  /// `continueTrace`. Presente solo dopo aver continuato un trace distribuito.
  traceParentId: string | undefined

  /// Flag di campionamento del trace W3C adottato (bit 0 di `trace-flags`).
  traceSampled: boolean | undefined

  /// `tracestate` W3C opaco (già validato/troncato) da ri-propagare.
  traceState: string | undefined

  /// `baggage` W3C opaco adottato: può contenere PII, perciò è ri-propagato SOLO
  /// verso destinazioni fidate e MAI allegato agli eventi.
  traceBaggage: string | undefined

  constructor(maxBreadcrumbs = 50) {
    this.breadcrumbs = new BreadcrumbBuffer(maxBreadcrumbs)
  }

  setUser(user: UserContext | null): void {
    this.user = user === null ? undefined : { ...user }
  }

  /// Id utente corrente come stringa (per la correlazione del session replay), o
  /// `undefined` se non impostato.
  get userId(): string | undefined {
    const id = this.user?.['id']
    return id === undefined || id === null ? undefined : String(id)
  }

  setTag(key: string, value: string): void {
    this.tags[key] = value
  }

  setTags(tags: Record<string, string>): void {
    Object.assign(this.tags, tags)
  }

  setExtra(key: string, value: unknown): void {
    this.extra[key] = value
  }

  setContext(key: string, value: Record<string, unknown>): void {
    this.contexts[key] = value
  }

  setTraceId(id: string | undefined): void {
    this.traceId = id
  }

  /// Adotta un trace context W3C in ingresso: il suo `trace-id` diventa il
  /// `traceId` di correlazione (così errori/log lo portano) e parent/sampling/
  /// tracestate/baggage sono conservati per la ri-propagazione verso host fidati.
  continueTrace(ctx: {
    traceId: string
    parentId: string
    sampled: boolean
    tracestate?: string | undefined
    baggage?: string | undefined
  }): void {
    this.traceId = ctx.traceId
    this.traceParentId = ctx.parentId
    this.traceSampled = ctx.sampled
    this.traceState = ctx.tracestate
    this.traceBaggage = ctx.baggage
  }

  setReplaySessionId(id: string | undefined): void {
    this.replaySessionId = id
  }

  /// Request context manuale: vince su quello rilevato automaticamente.
  setRequest(request: Record<string, unknown> | null): void {
    this.request = request === null ? undefined : { ...request }
  }

  addBreadcrumb(input: BreadcrumbInput): void {
    this.breadcrumbs.add(new Breadcrumb(input))
  }

  /// Crea uno scope isolato a partire da questo: copia user/tag/extra/contexts/
  /// request/replaySessionId in strutture nuove, così le mutazioni sul fork non
  /// toccano l'originale (né viceversa). È la base dell'isolamento per flusso
  /// asincrono su Node (CYJS-7).
  ///
  /// Con [opts.freshTrace] (per-richiesta: `runWithScope`) il fork parte come
  /// nuova unità di correlazione — `traceId` azzerato e nessun breadcrumb
  /// ereditato — pur conservando i dati di scope come base. Senza (contesto
  /// temporaneo: `withScope`) eredita `traceId` e una copia dei breadcrumb correnti.
  fork(opts: { freshTrace?: boolean } = {}): Scope {
    const forked = new Scope(this.breadcrumbs.maxSize)
    forked.user = this.user ? { ...this.user } : undefined
    forked.tags = { ...this.tags }
    forked.extra = { ...this.extra }
    forked.contexts = cloneContexts(this.contexts)
    forked.request = this.request ? { ...this.request } : undefined
    forked.replaySessionId = this.replaySessionId
    if (!(opts.freshTrace ?? false)) {
      forked.traceId = this.traceId
      // Il trace context W3C fa parte della stessa unità di correlazione del
      // traceId: ereditato col contesto temporaneo, azzerato con freshTrace.
      forked.traceParentId = this.traceParentId
      forked.traceSampled = this.traceSampled
      forked.traceState = this.traceState
      forked.traceBaggage = this.traceBaggage
      for (const crumb of this.breadcrumbs.toList()) forked.breadcrumbs.add(crumb)
    }
    return forked
  }

  clear(): void {
    this.user = undefined
    this.tags = {}
    this.extra = {}
    this.contexts = {}
    this.request = undefined
    this.traceId = undefined
    this.traceParentId = undefined
    this.traceSampled = undefined
    this.traceState = undefined
    this.traceBaggage = undefined
    this.breadcrumbs.clear()
  }

  /// Serializza in chiavi Sentry, includendo solo le sezioni valorizzate.
  /// Tags/extra/contexts/request passano per lo scrubber (difesa in profondità).
  /// `user` ha la sua policy dedicata (parità gem): senza `sendPii` si riduce a
  /// `{id}` (email & co. non partono proprio); con `sendPii` viaggia intero —
  /// impostarlo è una dichiarazione esplicita d'intento.
  toEventHash(opts: { sendPii?: boolean } = {}): EventPayload {
    const hash: EventPayload = {}

    if (this.user) {
      const user = (opts.sendPii ?? false) ? { ...this.user } : pickId(this.user)
      if (user) hash['user'] = user
    }
    if (Object.keys(this.tags).length > 0) hash['tags'] = scrub(this.tags)
    if (Object.keys(this.extra).length > 0) hash['extra'] = scrub(this.extra)
    if (Object.keys(this.contexts).length > 0) {
      hash['contexts'] = scrub(this.contexts)
    }
    if (this.request) hash['request'] = scrub(this.request)
    const crumbs = this.breadcrumbs.toJSON()
    if (crumbs) hash['breadcrumbs'] = crumbs

    return hash
  }
}

function pickId(user: Record<string, unknown>): Record<string, unknown> | undefined {
  return 'id' in user ? { id: user['id'] } : undefined
}

// Copia i contexts per chiave (un livello): `setContext` rimpiazza sempre l'intera
// chiave, quindi lo shallow-per-chiave basta a isolare il fork dall'originale.
function cloneContexts(
  contexts: Record<string, Record<string, unknown>>,
): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {}
  for (const key of Object.keys(contexts)) out[key] = { ...contexts[key] }
  return out
}
